import {flattenModuleCompositionRow} from "../../../../utils/flattenModuleCompositionRow";
import {getModuleCompositionDimension} from "../../../../utils/mapModuleCompositionToModuleItem";
import {
    getPacedDetailModuleId,
    parsePacedDetailModuleDto,
    stampCompositionDetailId,
    stampCompositionDetailList,
} from "../module3d/pacedDetailModule";
import {
    getPacedDtoHide,
    getPacedModuleId,
    parsePacedModuleDto,
} from "../module3d/pacedModule";
import {moduleDimensionsMmToSizeM} from "../module3d/snapPlacement";
import {axesToEulerDegrees, IDENTITY_AXES} from "../module3d/orientationAxes";
import {
    DEFAULT_TEXTURE_ROTATION_DEG,
    resolveDetailTextureUrl,
} from "../module3d/detailTextures";
import {pickCompositionTextureIdForDetail, getCompositionTextureScaleMmForDetail} from "../../../../utils/compositionTextureAssignment";
import {isCustomerModuleWidthLocked} from "../../../../utils/compositionMediaUtils";
import {
    getDetailSceneDrawOrder,
    sortDetailsBySequenceNumber,
} from "../../../../utils/processing/orderContourPoints";

const lookupTextureUrlById = (textureUrlById, id) => {
    if (id == null || textureUrlById == null) {
        return null;
    }
    const num = Number(id);
    if (Number.isFinite(num) && textureUrlById[num]) {
        return textureUrlById[num];
    }
    if (textureUrlById[id]) {
        return textureUrlById[id];
    }
    if (Number.isFinite(num) && textureUrlById[String(num)]) {
        return textureUrlById[String(num)];
    }
    return null;
};

/** Фото галереи композиции — не panel-текстуры (иначе слоты bodyTopTextureId и т.п. получают неверный URL). */
const isCompositionGalleryMediaUrl = (url) =>
    typeof url === "string" && url.includes("/organization_composition_media/");

export const buildTextureUrlByIdMap = (textures) => {
    const map = {};
    if (!Array.isArray(textures)) {
        return map;
    }
    for (const row of textures) {
        if (row?.id == null) continue;
        const url = row.relativeUrl || row.relativeUrlSmall;
        if (!url || isCompositionGalleryMediaUrl(url)) continue;
        const num = Number(row.id);
        if (Number.isFinite(num)) {
            map[num] = url;
        } else {
            map[row.id] = url;
        }
    }
    return map;
};

export const resolveCompositionDetailTextureUrl = (
    detail,
    textureUrlById = {},
    compositionTextures = null,
) => {
    const mappedId = pickCompositionTextureIdForDetail(detail, compositionTextures);
    if (mappedId != null) {
        const fromComposition = lookupTextureUrlById(textureUrlById, mappedId);
        if (fromComposition) {
            return fromComposition;
        }
        // Слот композиции задан, URL ещё грузится / by-textured не ответил —
        // не подставляем дефолтный дуб (resolveDetailTextureUrl), иначе «чужая» текстура.
        return null;
    }

    const direct =
        detail?.textureUrl
        || detail?.picture?.relativeUrl
        || detail?.picture?.relativeUrlSmall;
    if (direct) {
        return direct;
    }

    const fromDetailId = lookupTextureUrlById(textureUrlById, detail?.textureId);
    if (fromDetailId) {
        return fromDetailId;
    }

    return resolveDetailTextureUrl(detail);
};

const hasRealId = (value) => value != null && value !== "" && Number(value) !== 0;

/** Допуск привязки к плоскости стены в cube-local СК композиции (м). */
const PLACEMENT_WALL_EPS_M = 0.02;

const isCompositionCornerAnchor = (localPosition, compositionSizeM) => {
    const x = Number(localPosition?.x) || 0;
    const z = Number(localPosition?.z) || 0;
    const w = Math.max(Number(compositionSizeM?.width) || 0, 0.001);
    const onBackPlane = z >= -PLACEMENT_WALL_EPS_M && z <= PLACEMENT_WALL_EPS_M;
    const nearSideJunction = x <= PLACEMENT_WALL_EPS_M || x >= w - PLACEMENT_WALL_EPS_M;
    return onBackPlane && nearSideJunction;
};

/**
 * Прямая задняя стена (не угол): z ≈ 0, x между боковыми стыками.
 * pacedModule.axes с бэка зеркалит модуль — для этой зоны оставляем IDENTITY.
 */
export const isCompositionBackWallAnchor = (localPosition, compositionSizeM) => {
    if (!localPosition || !compositionSizeM) return false;
    const x = Number(localPosition.x) || 0;
    const z = Number(localPosition.z) || 0;
    const w = Math.max(Number(compositionSizeM.width) || 0, 0.001);
    const onBackPlane = z >= -PLACEMENT_WALL_EPS_M && z <= PLACEMENT_WALL_EPS_M;
    if (!onBackPlane || isCompositionCornerAnchor(localPosition, compositionSizeM)) {
        return false;
    }
    return x > PLACEMENT_WALL_EPS_M && x < w - PLACEMENT_WALL_EPS_M;
};

export const getCompositionModuleKey = (row, index = 0) =>
    row?.id
    ?? row?.moduleCompositionId
    ?? row?.moduleId
    ?? row?.module?.id
    ?? `composition-module-${index}`;

/** Строка moduleCompositions → единый вид (moduleId для API). */
export const normalizeCompositionModuleRow = (row) => {
    if (row == null || typeof row !== "object") return row;
    if (hasRealId(row.moduleId)) return row;
    if (row.module != null && typeof row.module === "object" && hasRealId(row.module.id)) {
        return flattenModuleCompositionRow(row);
    }
    return row;
};

/** id модуля для API (by-module-id, paced-modules) — не id строки в составе композиции. */
export const getCompositionModuleApiId = (row) => {
    const normalized = normalizeCompositionModuleRow(row);
    const moduleId = normalized?.moduleId ?? normalized?.module?.id;
    if (hasRealId(moduleId)) return moduleId;
    return hasRealId(normalized?.id) ? normalized.id : null;
};

const getModuleSizeM = (row) => {
    const normalized = normalizeCompositionModuleRow(row);
    const sizeMm = getModuleSizeMm(normalized);
    return moduleDimensionsMmToSizeM(sizeMm.width, sizeMm.height, sizeMm.depth);
};

const getModuleLabel = (row) =>
    row?.name ?? row?.module?.name ?? `Модуль ${getCompositionModuleApiId(row) ?? ""}`;

const getModuleSizeMm = (row) => ({
    width: Math.max(Number(getModuleCompositionDimension(row, "width")) || 600, 1),
    height: Math.max(Number(getModuleCompositionDimension(row, "height")) || 720, 1),
    depth: Math.max(Number(getModuleCompositionDimension(row, "depth")) || 560, 1),
});

const getCompositionModuleDetailKey = (detail, index) =>
    detail?.compositionDetailId ?? detail?.id ?? detail?.position ?? `detail-${index}`;

const asArray = (value) => (Array.isArray(value) ? value : []);

/** pacedModule в GET 3d лежит в module.pacedModule, в других ответах — на строке composition. */
export const resolveCompositionModulePacedModuleDto = (row) =>
    row?.pacedModule ?? row?.module?.pacedModule ?? null;

export const withCompositionModulePacedModuleRow = (row) => {
    if (row == null || typeof row !== "object") {
        return row;
    }
    const pacedModule = resolveCompositionModulePacedModuleDto(row);
    if (pacedModule == null || row.pacedModule === pacedModule) {
        return row;
    }
    return {...row, pacedModule};
};

const formatPointSignature = (point) => {
    if (!point || typeof point !== "object") return "";
    const x = Number(point.x) || 0;
    const y = Number(point.y) || 0;
    const z = Number(point.z) || 0;
    return `${x},${y},${z}`;
};

const formatAxesSignature = (axes) => {
    if (!axes || typeof axes !== "object") return "";
    const part = (tuple) => (Array.isArray(tuple) ? tuple.join(",") : "");
    return `${part(axes.x)}|${part(axes.y)}|${part(axes.z)}`;
};

/** Организация: только blockSize; заказчик: TOP всегда + blockSize на нижних. */
export const isCompositionModuleWidthBlocked = (row, {customerRules = false} = {}) => {
    if (customerRules) {
        return isCustomerModuleWidthLocked(row);
    }
    return Boolean(row?.module?.blockSize ?? row?.blockSize);
};

export const isCompositionModuleSizeBlocked = (row, options) =>
    isCompositionModuleWidthBlocked(row, options);

export const formatCompositionModuleSizeParts = (row) => {
    const {width, height, depth} = getModuleSizeMm(row);
    const format = (value) => String(Math.round(Number(value) * 10) / 10);
    return {
        width: format(width),
        height: format(height),
        depth: format(depth),
    };
};

export const getCompositionModuleRowSceneSignature = (row, index = 0) => {
    const key = getCompositionModuleKey(row, index);
    const sizeMm = getModuleSizeMm(row);
    const sizePart = `${sizeMm.width}:${sizeMm.height}:${sizeMm.depth}`;
    const pacedDto = resolveCompositionModulePacedModuleDto(row);
    const paced = parsePacedModuleDto(pacedDto);
    const pacedPart = paced?.localPosition
        ? [
            formatPointSignature(paced.localPosition),
            formatAxesSignature(paced.axes || IDENTITY_AXES),
            paced.faceFlip ? "1" : "0",
            getPacedDtoHide(pacedDto) ? "1" : "0",
            getPacedModuleId(row) ?? "",
        ].join(":")
        : "unplaced";

    const detailsPart = flattenModuleDetailPatterns(row).map((detail, detailIndex) => {
        const detailId = getCompositionModuleDetailKey(detail, detailIndex);
        const pacedDetail = parsePacedDetailModuleDto(detail?.pacedDetailModule);
        if (!pacedDetail?.localPosition) {
            return `${detailId}:unplaced`;
        }
        return [
            detailId,
            formatPointSignature(pacedDetail.localPosition),
            formatAxesSignature(pacedDetail.axes || IDENTITY_AXES),
            pacedDetail.faceFlip ? "1" : "0",
            getPacedDtoHide(detail?.pacedDetailModule) ? "1" : "0",
            detail?.textureId ?? "",
        ].join(":");
    }).join(";");

    const markerPart = [
        row?.coordinateX3DPoint ?? "",
        row?.coordinateY3DPoint ?? "",
        row?.priority ?? "",
    ].join(":");

    return `${key}|${sizePart}|${pacedPart}|${detailsPart}|${markerPart}`;
};

export const getCompositionSceneSignature = (rows) => (
    Array.isArray(rows)
        ? rows.map((row, index) => getCompositionModuleRowSceneSignature(row, index)).join("||")
        : ""
);

/**
 * Состав модуля из GET композиции: module.detailPatterns[].details[].
 * Для FL-сцены достаточно length/width/height + вложенного pacedDetailModule.
 */
export const flattenModuleDetailPatterns = (row) => {
    const normalized = normalizeCompositionModuleRow(row);
    if (!normalized) return [];
    const patterns = asArray(
        normalized.module?.detailPatterns ?? normalized.detailPatterns,
    );
    const details = [];
    for (const pattern of patterns) {
        for (const detail of asArray(pattern?.details)) {
            if (detail != null && typeof detail === "object") {
                details.push(detail);
            }
        }
    }
    return stampCompositionDetailList(details);
};

export const buildCompositionModuleChildFromDetail = (
    detail,
    index = 0,
    textureUrlById = {},
    compositionTextures = null,
) => {
    const row = stampCompositionDetailId(detail);
    if (!row || getPacedDtoHide(row?.pacedDetailModule)) return null;
    const paced = parsePacedDetailModuleDto(row?.pacedDetailModule);
    if (!paced?.localPosition || !paced?.axes) return null;
    const axes = paced.axes;

    const detailKey = getCompositionModuleDetailKey(row, index);
    const pacedId = getPacedDetailModuleId(row);

    return {
        instanceId: pacedId != null ? `composition-child-${pacedId}` : `composition-child-${detailKey}`,
        detail: row,
        detailData: row,
        detailKey,
        localPosition: paced.localPosition,
        axes,
        faceFlip: Boolean(paced.faceFlip),
        textureId: row?.textureId ?? null,
        textureUrl: resolveCompositionDetailTextureUrl(row, textureUrlById, compositionTextures),
        textureScaleMm: getCompositionTextureScaleMmForDetail(row),
        textureRotationDeg: DEFAULT_TEXTURE_ROTATION_DEG,
    };
};

/** Детали модуля на FL-сцене — только из GET композиции (detailPatterns). */
export const buildCompositionModuleChildren = (
    moduleRow,
    textureUrlById = {},
    compositionTextures = null,
) => {
    const details = moduleRow ? flattenModuleDetailPatterns(moduleRow) : [];
    const sortedDetails = sortDetailsBySequenceNumber(details);
    const children = sortedDetails
        .map((detail, index) => {
            const child = buildCompositionModuleChildFromDetail(
                stampCompositionDetailId(detail),
                index,
                textureUrlById,
                compositionTextures,
            );
            if (!child) {
                return null;
            }
            return {
                ...child,
                sceneDrawOrder: getDetailSceneDrawOrder(detail, index),
            };
        })
        .filter(Boolean);
    return {children, detailsCount: details.length};
};

const attachCompositionModuleChildren = (
    inst,
    textureUrlById = {},
    compositionTextures = null,
) => {
    if (!inst?.row) return inst;
    const {children, detailsCount} = buildCompositionModuleChildren(
        inst.row,
        textureUrlById,
        compositionTextures,
    );
    return {
        ...inst,
        children,
        childrenHydrated: true,
        childrenLoadFailed: false,
        moduleDetailsCount: detailsCount,
        moduleDetailsLoaded: true,
    };
};

const buildCompositionModuleInstanceCore = ({
    row,
    index = 0,
    localPosition,
    axes,
    faceFlip = false,
    children = [],
    pacedModule = null,
    instanceId = null,
}) => {
    const normalizedRow = normalizeCompositionModuleRow(row);
    const moduleKey = getCompositionModuleKey(normalizedRow, index);
    const pacedId = pacedModule ? getPacedModuleId({pacedModule}) : null;
    const moduleSizeM = getModuleSizeM(normalizedRow);
    const resolvedAxes = axes || IDENTITY_AXES;

    return {
        instanceId: instanceId
            ?? (pacedId != null ? `composition-paced-${pacedId}` : `composition-${moduleKey}`),
        moduleKey,
        moduleApiId: getCompositionModuleApiId(normalizedRow),
        label: getModuleLabel(normalizedRow),
        row: normalizedRow,
        localPosition,
        axes: resolvedAxes,
        faceFlip: Boolean(faceFlip),
        euler: axesToEulerDegrees(resolvedAxes),
        moduleSizeM,
        children,
        pacedModuleId: pacedId,
        pacedModule,
        hidden: getPacedDtoHide(pacedModule ?? row?.pacedModule),
        childrenHydrated: false,
    };
};

export const buildPlacedCompositionModuleShellFromPose = ({
    row,
    index = 0,
    localPosition,
    axes,
    faceFlip = false,
    pacedModule = null,
    instanceId = null,
    textureUrlById = {},
    compositionTextures = null,
}) => {
    if (!row || !localPosition) return null;
    const resolvedAxes = axes || IDENTITY_AXES;
    return attachCompositionModuleChildren(buildCompositionModuleInstanceCore({
        row: normalizeCompositionModuleRow(row),
        index,
        localPosition,
        axes: resolvedAxes,
        faceFlip,
        children: [],
        pacedModule,
        instanceId,
    }), textureUrlById, compositionTextures);
};

export const isCompositionModulePlacedDto = (row) => {
    const paced = parsePacedModuleDto(resolveCompositionModulePacedModuleDto(row));
    return Boolean(paced?.localPosition);
};

/**
 * Поза модуля на сцене композиции из pacedModule.
 * Задняя стена — только anchor (IDENTITY); боковая — anchor + axesX/Y/Z с бэка.
 */
export const getCompositionModulePlacementPose = (row, {compositionSizeM = null} = {}) => {
    const pacedModuleDto = resolveCompositionModulePacedModuleDto(row);
    const paced = parsePacedModuleDto(pacedModuleDto);
    if (!paced?.localPosition) return null;

    if (isCompositionBackWallAnchor(paced.localPosition, compositionSizeM)) {
        return {
            localPosition: paced.localPosition,
            axes: IDENTITY_AXES,
            faceFlip: false,
        };
    }

    return {
        localPosition: paced.localPosition,
        axes: paced.axes || IDENTITY_AXES,
        faceFlip: Boolean(paced.faceFlip),
    };
};

/**
 * Поза модуля на сцене композиции — из GET /compositions/{id}:
 * anchorPoint + axesX/Y/Z в СК куба композиции.
 */
export const buildPlacedCompositionModuleIfPlaced = (
    row,
    index = 0,
    {compositionSizeM = null, textureUrlById = {}, compositionTextures = null} = {},
) => {
    const rowForScene = withCompositionModulePacedModuleRow(row);
    if (!isCompositionModulePlacedDto(rowForScene)) {
        return {inst: null, rowWithPlacement: rowForScene};
    }
    const pacedModuleDto = resolveCompositionModulePacedModuleDto(rowForScene);
    const pose = getCompositionModulePlacementPose(rowForScene, {compositionSizeM});
    const inst = buildPlacedCompositionModuleShellFromPose({
        row: rowForScene,
        index,
        localPosition: pose.localPosition,
        axes: pose.axes,
        faceFlip: pose.faceFlip,
        pacedModule: pacedModuleDto,
        textureUrlById,
        compositionTextures,
    });
    return {inst, rowWithPlacement: rowForScene};
};

/** Габариты для snap при клике по сцене композиции. */
export const compositionModulePlacementDetailData = (row) => {
    const sizeMm = getModuleSizeMm(row);
    return {
        length: sizeMm.width,
        width: sizeMm.depth,
        height: sizeMm.height,
    };
};

export const formatCompositionModuleSize = (row) => {
    const {width, height, depth} = getModuleSizeMm(row);
    return [width, height, depth]
        .map((value) => String(Math.round(value * 10) / 10))
        .join(" × ");
};

/** Габариты зелёного куба композиции: lengthLeft × height × lengthRight (мм). */
export const getCompositionCubeSizeMm = ({
    lengthLeft,
    lengthRight,
    height,
    cornerType,
} = {}) => {
    const left = Math.max(Number(lengthLeft) || 0, 1);
    const right = Math.max(Number(lengthRight) || 0, 1);
    const h = Math.max(Number(height) || 0, 1);

    const normalizedCorner = cornerType === "CORNER__LEFT"
        ? "CORNER_LEFT"
        : cornerType === "CORNER__RIGHT"
            ? "CORNER_RIGHT"
            : cornerType;

    if (normalizedCorner === "STRAIGHT") {
        return {
            width: left,
            height: h,
            depth: right > 1 ? right : left,
        };
    }

    // Как в getCornerModuleRowConfigs: для CORNER_LEFT «слева» → depth, «справа» → width.
    if (normalizedCorner === "CORNER_LEFT") {
        return {
            width: right,
            height: h,
            depth: left,
        };
    }

    if (normalizedCorner === "CORNER_RIGHT") {
        return {
            width: left,
            height: h,
            depth: right,
        };
    }

    return {
        width: right,
        height: h,
        depth: left,
    };
};

export const getCompositionCubeSizeM = (dimensions) => {
    const {width, height, depth} = getCompositionCubeSizeMm(dimensions);
    return moduleDimensionsMmToSizeM(width, height, depth);
};
